import { z } from "zod";
import { number as zodNumber } from "src/utils/schema";

// ---------------------------------------------------------------------------
// Shared Zod base
// ---------------------------------------------------------------------------

export const basePathSchema = z.object({
  search: z.any().optional(),
  hash: z.any().optional(),
});

// ---------------------------------------------------------------------------
// Route param schemas — derived from route definitions via appRoutes
// ---------------------------------------------------------------------------

/** Routes that require only env_name (e.g. apps, runs, compute, datasets, alerts, dashboard) */
export const envPath = basePathSchema.extend({
  env_name: z.string(),
});

export const appPath = basePathSchema.extend({
  env_name: z.string(),
  app_name: z.string(),
});

export const pitPath = basePathSchema.extend({
  env_name: z.string(),
  app_name: z.string(),
  app_pit: z.string(),
});

export const taskPath = basePathSchema.extend({
  env_name: z.string(),
  app_name: z.string(),
  task_name: z.string(),
});

export const taskRunPath = basePathSchema.extend({
  env_name: z.string(),
  app_name: z.string(),
  app_pit: z.string(),
  task_id: zodNumber,
});

export const transformationPath = basePathSchema.extend({
  env_name: z.string(),
  app_name: z.string(),
  app_pit: z.string(),
  task_id: zodNumber,
  tf_id: z.number(),
});

export const clusterPath = basePathSchema.extend({
  env_name: z.string(),
  app_name: z.string(),
  cluster_name: z.string(),
});

export const clusterRunPath = basePathSchema.extend({
  env_name: z.string(),
  app_name: z.string(),
  cluster_name: z.string(),
  run_time: z.string(),
  task_id: z.number(),
});

export const datasetPath = basePathSchema.extend({
  dataset: z.string(),
});

// ---------------------------------------------------------------------------
// Redirect-only schemas — shortcut URLs resolved entirely by router loaders,
// with no corresponding page in the route registry.
// ---------------------------------------------------------------------------

/** DB `run_status` enum values accepted by `/task-runs?status=` / GET_TASK_IDS */
export const TASK_RUN_API_STATUSES = [
  "COMPLETED",
  "RUNNING",
  "PLANNED",
  "FAILED",
  "DISABLED",
  "UNKNOWN",
] as const;

export type TaskRunApiStatus = (typeof TASK_RUN_API_STATUSES)[number];

export const taskRedirectPath = basePathSchema.extend({
  task_id: z.number(),
  redirect: z.literal(true),
});

export const taskRedirectByNameAndPitPath = basePathSchema.extend({
  env_name: z.string(),
  app_name: z.string(),
  task_name: z.string(),
  app_pit: z.string().or(z.literal("latest")),
  redirect: z.literal(true),
  status: z.enum(TASK_RUN_API_STATUSES).optional(),
});

export const computeRedirectByNameAndPitPath = basePathSchema.extend({
  env_name: z.string(),
  app_name: z.string(),
  cluster_name: z.string(),
  run_time: z.string().or(z.literal("latest")),
  redirect: z.literal(true),
});

export const transformRedirectPath = basePathSchema.extend({
  // TODO cast from string in case it comes from a url param
  tf_id: z.number(),
  redirect: z.literal(true),
});

export const pathParamsSchema = z.union([
  datasetPath,
  transformRedirectPath,
  taskRedirectPath,
  taskRedirectByNameAndPitPath,
  computeRedirectByNameAndPitPath,
  appPath,
  pitPath,
  taskPath,
  taskRunPath,
  transformationPath,
  clusterPath,
  clusterRunPath,
]);
